<template>
  <div class="mobile-pretest" :style="themeVariables">
    <main class="mobile-pretest-content">
      <section class="waiting-panel">
        <h2>候考</h2>
        <p class="waiting-description">考试开始前，请考生在此页面耐心等待。</p>
        <div v-if="examReady && testInfo.start_time && !hasNoTest" class="countdown-panel" role="timer" aria-label="距离开考">
          <countdown
            :key="countdownKey"
            class="countdown-value"
            :remote-time="testInfo.start_time"
            @listen="listen"
            @sync-state="onClockSync"
          ></countdown>
          <div class="countdown-units"><span>小时</span><span>分钟</span><span>秒</span></div>
        </div>

        <div class="info-panel">
          <h3>考生信息</h3>
          <div v-if="!hasNoTest && infoRows.length" class="info-list">
            <div
              v-for="row in infoRows"
              :key="row.label"
              class="info-row"
              :class="{ 'is-wide': row.label === '考试名称' || row.label === '开考时间' }"
            >
              <span class="info-label">{{ row.label }}：</span>
              <span class="info-value">{{ row.value }}</span>
            </div>
          </div>
          <div v-else class="empty-info">考试卷正在配置中</div>
        </div>

        <p v-if="testInfo.isLate" class="waiting-notice is-late">迟到{{ testInfo.last_come_in_minute }}分钟，已不能进入考场考试</p>
        <p v-else-if="canShowRetake" class="waiting-notice is-retake">该考试已完成交卷，重考将会清除上次考试作答记录</p>
        <p v-else-if="showTime && !hasNoTest" class="waiting-notice"><icon-bell class="notice-icon" aria-hidden="true"></icon-bell>考试开始前，请在此页面耐心等待</p>
        <p v-if="needIdentity" class="identity-tip">如退出当前页面，考生需重新登录进行身份核验</p>

        <button
          v-if="hasNoTest"
          class="action-button primary"
          type="button"
          @click="close"
        >知道了</button>
        <button
          v-else-if="canShowRetake"
          class="action-button primary"
          type="button"
          :disabled="retakeButtonDisabled"
          :class="{ 'is-loading': retakeLoading }"
          :aria-busy="retakeLoading"
          @click="resetAndStartTest"
        >
          <loading v-if="retakeLoading" :box-size="3" :round-size="1" :color="'#fff'"></loading>
          <span v-else>重考</span>
        </button>
        <button
          v-else
          class="action-button primary"
          type="button"
          :disabled="startButtonDisabled"
          :class="{ 'is-loading': btnLoading }"
          :aria-busy="btnLoading"
          @click="startTest"
        >
          <loading v-if="btnLoading" :box-size="3" :round-size="1" :color="'#fff'"></loading>
          <span v-else>{{ showTime ? '暂未开始' : '开始考试' }}</span>
        </button>
      </section>
    </main>

    <detect-tip-modal
      ref="detectRef"
      v-show="showDetectModal"
      @close="showDetectModal = false"
      @goTest="startTest(false)"
    ></detect-tip-modal>
    <commitment-modal
      v-if="examReady"
      :key="activeExamId"
      v-show="showCommitmentModal"
      @close="showCommitmentModal = false"
      @goTest="startTest(false)"
    ></commitment-modal>
  </div>
</template>

<script>
import PcPretest from './pc'
import IconBell from '@/assets/svg/icon-bell.svg'

export default {
  name: 'MobilePretest',
  extends: PcPretest,
  components: { IconBell },
  computed: {
    themeVariables() {
      const normalize = value => String(value || '').replace(/;+\s*$/, '')
      const color = normalize(this.theme.themeColor) || '#377dff'
      return {
        '--mobile-theme': color,
        '--mobile-theme-light': normalize(this.theme.themeSubColor) || '#f2fffb',
        '--mobile-button-background': normalize(this.theme.buttonBackground) || color
      }
    },
    infoRows() {
      const info = this.testInfo || {}
      if (this.shandongKeXieInfo) {
        return [
          ['开考时间', info.start_time && this.$options.filters.date(info.start_time)],
          ['考试时长', info.exam_time && `${info.exam_time}分钟`],
          ['赛级', this.shandongKeXieInfo.level],
          ['组别', this.shandongKeXieInfo.group],
          ['赛项', this.shandongKeXieInfo.event]
        ].filter((row) => row[1]).map((row) => ({ label: row[0], value: row[1] }))
      }

      const rows = []
      if (info.exam_name) rows.push({ label: '考试名称', value: info.exam_name })
      if (info.name && (this.testConfig.show_examinee_name || this.isTouristsId)) {
        rows.push({ label: this.isChongQing ? '用户名' : '考生姓名', value: info.name })
      }
      if (info.start_time && this.$route.path.split('/')[1].indexOf(this.weishengRoute) < 0) {
        rows.push({ label: '开考时间', value: this.$options.filters.date(info.start_time) })
      }
      if (info.exam_time) rows.push({ label: '考试时长', value: `${info.exam_time}分钟` })
      if (info.school) rows.push({ label: '所在学校', value: info.school })
      if (info.identity_num) rows.push({ label: '身份证号码', value: info.identity_num })
      if (info.mobile) rows.push({ label: '手机号码', value: info.mobile })
      if (info.score) rows.push({ label: '卷面总分', value: `${info.score}分` })
      if (info.area) rows.push({ label: '所在地区', value: info.area })
      return rows
    }
  }
}
</script>

<style lang="scss" scoped>
.mobile-pretest {
  box-sizing: border-box;
  min-height: 100%;
  color: #333;
  background: var(--mobile-theme-light);
}

.mobile-pretest-content {
  width: 100%;
  max-width: 37.5rem;
  margin: 0 auto;
  padding: 2rem 1rem 2.5rem;
}

.waiting-panel {
  width: 100%;
  padding: 2rem 1rem 2.625rem;
  text-align: center;
  background: #fff;
  border-radius: 0.5rem;
  box-shadow: 0 0.4rem 1.25rem rgba(21, 96, 71, 0.06);
}

.waiting-panel h2 {
  margin: 0;
  color: #222;
  font-size: 1.5rem;
  font-weight: 600;
  line-height: 1.4;
}

.waiting-description {
  margin: 0.5rem 0 0;
  color: #666;
  font-size: 0.875rem;
  line-height: 1.45;
}

.countdown-panel {
  width: 100%;
  margin-top: 0.75rem;
}

.countdown-value {
  height: 3.35rem;
  color: var(--mobile-theme);
  font-size: 2.5rem;
  font-weight: 400;
  line-height: 3.35rem;
  letter-spacing: 0;
  white-space: nowrap;
}

.countdown-value /deep/ span {
  color: var(--mobile-theme) !important;
}

.countdown-units {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  width: 14rem;
  margin: 0 auto;
  color: var(--mobile-theme);
  font-size: 0.75rem;
  line-height: 1.4;
}

.identity-tip {
  margin: 0.65rem 0 0;
  color: #999;
  font-size: 0.68rem;
  line-height: 1.4;
}

.info-panel {
  margin-top: 1.1875rem;
  padding: 1.25rem 1.25rem 1.1rem;
  text-align: left;
  background: #fafafa;
  border-radius: 0.25rem;
}

.info-panel h3 {
  margin: 0 0 1rem;
  color: #222;
  font-size: 1.125rem;
  font-weight: 600;
  line-height: 1.4;
  text-align: center;
}

.info-list {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
}

.info-row {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  min-width: 0;
  font-size: 0.875rem;
  line-height: 1.45;
}

.info-label {
  flex: 0 0 6rem;
  color: #999;
  text-align: right;
  white-space: nowrap;
}

.info-value {
  flex: 1;
  min-width: 0;
  color: #333;
  font-weight: 500;
  overflow-wrap: break-word;
  word-break: break-word;
  overflow-wrap: anywhere;
}

.empty-info {
  padding: 1.25rem 0;
  color: #999;
  font-size: 0.78rem;
  text-align: center;
}

.waiting-notice {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  min-height: 2.4rem;
  padding: 0.55rem 0.9rem;
  margin: 1rem 0 0;
  color: #333;
  font-size: 0.875rem;
  line-height: 1.4;
  text-align: left;
  background: #fff9e9;
  border: 1px solid #ffc640;
  border-radius: 0.25rem;

  &.is-late,
  &.is-retake {
    color: #f5222d;
    background: #fff1f0;
    border-color: #ffccc7;
  }
}

.notice-icon {
  flex: 0 0 auto;
  width: 1.0625rem;
  height: 1.125rem;
  fill: #f2a900;
}

.action-button {
  width: 100%;
  height: 2.5rem;
  margin-top: 1.5rem;
  color: #fff;
  border: 0;
  border-radius: 0.25rem;
  background: var(--mobile-button-background, var(--mobile-theme));
  font: inherit;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
}

.action-button:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.action-button.is-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 1;
}

@media only screen and (min-width: 48rem) and (max-width: 80rem) {
  .mobile-pretest-content {
    max-width: 22rem;
    padding: 0.75rem 1rem 1rem;
  }

  .waiting-panel {
    padding: 1rem 1.5rem 1.25rem;
  }

  .waiting-panel h2 {
    font-size: 1.2rem;
  }

  .waiting-description,
  .info-row,
  .waiting-notice {
    font-size: 0.7rem;
  }

  .countdown-value {
    height: 2.75rem;
    font-size: 2rem;
    line-height: 2.75rem;
  }

  .countdown-units {
    width: 11.5rem;
    font-size: 0.62rem;
  }

  .info-panel {
    margin-top: 0.75rem;
    padding: 0.75rem 1rem;
  }

  .info-panel h3 {
    margin-bottom: 0.8rem;
    font-size: 0.9rem;
  }

  .info-list {
    grid-template-columns: minmax(0, 1fr);
    gap: 0.5rem 0.75rem;
  }

  .info-label {
    flex-basis: 4.5rem;
  }

  .info-row.is-wide {
    grid-column: 1 / -1;
  }

  .notice-icon {
    width: 0.65rem;
    height: 0.7rem;
  }

  .waiting-notice {
    min-height: 2rem;
    margin-top: 0.65rem;
  }

  .action-button {
    height: 2.25rem;
    margin-top: 0.75rem;
    font-size: 0.75rem;
  }
}
</style>
